'use client';

import { useLocale, useTranslations } from 'next-intl';
import React from 'react';

import HorizontalMovieList from '@/components/HorizontalMovieList';
import ListPlaceholderSimple from '@/components/list/ListPlaceholderSimple';

import { ApiResponse } from '@cineamo/api/types/ApiResponse.types';

import { getMoviesComingSoon } from '@/helpers/page-api-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';
import { usePagination } from '@/hooks/usePagination';

import CenterLayout from '@cineamo/composables/src/CenterLayout';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type ComingSoonTabPanelProps = {
    moviesComingSoonResponse?: ApiResponse<unknown, { 'cinema-content-upcomings': CachedContentData[] }>;
    onSelectedContentChange: (current: CachedContentData, next: CachedContentData, isFading: boolean) => void;
    isAutoScrollingEnabled: boolean;
    isEnabled: boolean;
    onPointerDown: () => void;
};

const ComingSoonTabPanel: React.FC<ComingSoonTabPanelProps> = ({
    moviesComingSoonResponse,
    onSelectedContentChange,
    isAutoScrollingEnabled,
    isEnabled,
    onPointerDown
}) => {
    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();
    const locale = useLocale();
    const cinema = domainConfiguration.cinema;
    const cinemaId = cinema.id;

    const {
        ref,
        data: contentListResponse,
        isFetching: isFetchingContentList,
        // isFetchingNextPage,
        hasNextPage
        // hasPreviousPage,
        // refetch
    } = usePagination<{ 'cinema-content-upcomings': CachedContentData[] }>(
        ['movies-coming-soon'],
        async (pageParam) => {
            return getMoviesComingSoon(cinemaId, cinema, locale, pageParam);
        },
        moviesComingSoonResponse && {
            initialData: {
                pages: [moviesComingSoonResponse],
                pageParams: [1]
            }
        }
    );

    const contentsList: CachedContentData[] =
        contentListResponse.pages.flatMap((page) => page._embedded?.['cinema-content-upcomings'] ?? []) ?? [];
    const emptyData = contentsList?.length === 0;

    return (
        <CenterLayout className="flex flex-col">
            {emptyData ? (
                <ListPlaceholderSimple text={t('home.currently-no-screenings-in-cinemas')} />
            ) : (
                <HorizontalMovieList
                    contents={contentsList}
                    onSelectedContentChange={onSelectedContentChange}
                    isAutoScrollingEnabled={isAutoScrollingEnabled}
                    isEnabled={isEnabled}
                    onPointerDown={onPointerDown}
                    paginationHasNextPage={hasNextPage}
                    paginationLoaderRef={ref}
                    paginationIsFetching={isFetchingContentList}
                    showReleaseDate={true}
                    analytics={{
                        track: {
                            sourceName: 'Coming Soon Movies List'
                        }
                    }}
                />
            )}
        </CenterLayout>
    );
};

export default ComingSoonTabPanel;
